import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@repo/shared/domain';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getVerifyPermission, constructQuery } from '@repo/shared/helpers';
import { GeneralNotFound } from '@repo/components';
import { DocumentViewer } from '@repo/components';
import { Spinner } from '@repo/components';
import { getPartnerCompanyEventDocuments } from 'src/api/partner';
import { useBreadcrumbLevel } from '@repo/components/hooks';

export const Route = createFileRoute('/partner_/$partnerId/events/$eventId/documents/$documentId')({
	component: RouteComponent,
});

function RouteComponent() {
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const { partnerId, eventId, documentId } = Route.useParams();
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	useBreadcrumbLevel(3, 'Documents', `/partner/${partnerId}/events/${eventId}/documents`);
	useBreadcrumbLevel(4, 'Document Details');

	const query = constructQuery({ all: 'yes', includeLogs: 'true' });
	const {
		data: documentsResponse,
		isLoading: documentsLoading,
		error: _getPartnerCompanyEventDocumentsError,
	} = useQuery({
		queryKey: ['partner-company-event-documents', partnerId, eventId, 'all'],
		queryFn: () => getPartnerCompanyEventDocuments(partnerId, eventId, query),
		enabled: !userLoading && !!authenticatedUser,
	});
	const document = documentsResponse?.data?.find((document) => document.id === documentId);

	if (userLoading || documentsLoading) {
		return (
			<div className="flex items-center justify-center h-full">
				<Spinner className="text-[#1E1E1E]" />
			</div>
		);
	}

	if (!document) {
		return (
			<GeneralNotFound
				label="We couldn't find this document. Maybe it has been deleted?"
				action={{
					label: 'Return to documents',
					href: `/partner/${partnerId}/events/${eventId}/documents`,
					variant: 'outlined',
				}}
			/>
		);
	}

	const handleRevalidateDocument = async () => {
		// Invalidate this specific document
		await queryClient.invalidateQueries({
			queryKey: ['partner-company-document', partnerId, documentId],
		});
	};

	const handleClose = () => {
		navigate({
			to: '/partner/$partnerId/events/$eventId/documents',
			params: { partnerId, eventId },
		});
		return null;
	};

	return (
		<DocumentViewer
			document={document}
			onClose={handleClose}
			types={[]}
			apiUrlBase={`/api/partner-portal/groups/${partnerId}/events/${eventId}/files`}
			onRevalidateDocument={handleRevalidateDocument}
			tasks={[]}
			permissions={{
				metadata: verifyPermission([PERMISSIONS.partnerCompanyEventsView]),
				fullView: verifyPermission([PERMISSIONS.partnerCompanyEventsView]),
				sensitiveView: false,
				sensitiveEdit: false,
				sensitiveArchive: false,
				edit: verifyPermission([PERMISSIONS.partnerCompanyDocumentsEdit]),
				archive: verifyPermission([PERMISSIONS.partnerCompanyDocumentsEdit]),
				review: false,
			}}
		/>
	);
}
